/**
 * 学習スケジュール PDF エクスポートユーティリティ
 * Canvas直接描画方式 — ずれゼロ・見切れゼロ保証
 */

import { StudyGoal } from '../hooks/useStudyGoals';

export interface ScheduleExportData {
  username: string;
  dailyGoals: StudyGoal[];
  weekDates: string[];
  weekLabel: string;
  periodGoals: {
    weekly: StudyGoal[];
    monthly: StudyGoal[];
    quarterly: StudyGoal[];
    yearly: StudyGoal[];
  };
  periodLabels: {
    weekly: string;
    monthly: string;
    quarterly: string;
    yearly: string;
  };
}

// ============================================================
// Canvas描画ユーティリティ
// ============================================================

/** 角丸矩形を描画 */
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  if (w < 2 * r) r = w / 2;
  if (h < 2 * r) r = h / 2;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** グラデーション塗りつぶし（左→右） */
function fillGradientH(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  c1: string,
  c2: string,
) {
  const g = ctx.createLinearGradient(x, y, x + w, y);
  g.addColorStop(0, c1);
  g.addColorStop(1, c2);
  ctx.fillStyle = g;
  ctx.fillRect(x, y, w, h);
}

/** グラデーション塗りつぶし（上→下） */
function fillGradientV(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  c1: string,
  c2: string,
) {
  const g = ctx.createLinearGradient(x, y, x, y + h);
  g.addColorStop(0, c1);
  g.addColorStop(1, c2);
  ctx.fillStyle = g;
  ctx.fillRect(x, y, w, h);
}

/** テキストを指定幅で折り返して描画。実際に描画した行数を返す */
function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines: number,
): number {
  const words = text.split('');
  let line = '';
  let lineCount = 0;

  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i];
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && line !== '') {
      if (lineCount >= maxLines - 1) {
        // 最終行は省略記号
        let truncated = line;
        while (ctx.measureText(truncated + '…').width > maxWidth && truncated.length > 0) {
          truncated = truncated.slice(0, -1);
        }
        ctx.fillText(truncated + '…', x, y + lineCount * lineHeight);
        lineCount++;
        return lineCount;
      }
      ctx.fillText(line, x, y + lineCount * lineHeight);
      lineCount++;
      line = words[i];
    } else {
      line = testLine;
    }
  }
  if (line) {
    ctx.fillText(line, x, y + lineCount * lineHeight);
    lineCount++;
  }
  return lineCount;
}

/** 円弧プログレスを描画 */
function drawArcProgress(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  lineWidth: number,
  pct: number,
  trackColor: string,
  progressColor: string,
) {
  const startAngle = -Math.PI / 2;
  const endAngle = startAngle + (pct / 100) * 2 * Math.PI;

  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
  ctx.strokeStyle = trackColor;
  ctx.lineWidth = lineWidth;
  ctx.stroke();

  if (pct > 0) {
    ctx.beginPath();
    ctx.arc(cx, cy, radius, startAngle, endAngle);
    ctx.strokeStyle = progressColor;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.lineCap = 'butt';
  }
}

// ============================================================
// メイン描画関数
// ============================================================

const isToday = (dateStr: string): boolean => {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return dateStr === `${y}-${m}-${d}`;
};

function drawSchedulePDF(data: ScheduleExportData): HTMLCanvasElement {
  // A4 @ 2x scale = 1587 x 2245
  const SCALE = 2;
  const PAGE_W = 794 * SCALE;
  const PAGE_H = 1123 * SCALE;

  const canvas = document.createElement('canvas');
  canvas.width = PAGE_W;
  canvas.height = PAGE_H;
  const ctx = canvas.getContext('2d')!;

  // スケール適用
  ctx.scale(SCALE, SCALE);

  const W = 794;
  const H = 1123;

  // ============================================================
  // 背景
  // ============================================================
  fillGradientV(ctx, 0, 0, W, H, '#080d1a', '#0d1525');

  // 装飾グロー（右上）
  const glowR = ctx.createRadialGradient(680, 80, 0, 680, 80, 320);
  glowR.addColorStop(0, 'rgba(13,148,136,0.12)');
  glowR.addColorStop(1, 'rgba(13,148,136,0)');
  ctx.fillStyle = glowR;
  ctx.fillRect(0, 0, W, H);

  // 装飾グロー（左下）
  const glowL = ctx.createRadialGradient(100, 900, 0, 100, 900, 280);
  glowL.addColorStop(0, 'rgba(16,185,129,0.08)');
  glowL.addColorStop(1, 'rgba(16,185,129,0)');
  ctx.fillStyle = glowL;
  ctx.fillRect(0, 0, W, H);

  // 微細グリッド
  ctx.strokeStyle = 'rgba(255,255,255,0.025)';
  ctx.lineWidth = 0.5;
  for (let x = 0; x < W; x += 60) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
  }
  for (let y = 0; y < H; y += 60) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
  }

  // ============================================================
  // データ準備
  // ============================================================
  const goalsByDate: Record<string, StudyGoal[]> = {};
  data.weekDates.forEach(d => (goalsByDate[d] = []));
  data.dailyGoals.forEach(g => {
    if (goalsByDate[g.date]) goalsByDate[g.date].push(g);
  });

  const totalGoals = data.dailyGoals.length;
  const completedGoals = data.dailyGoals.filter(g => g.is_completed).length;
  const completionRate = totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0;
  const activeDays = data.weekDates.filter(d => (goalsByDate[d] || []).length > 0).length;
  const perfectDays = data.weekDates.filter(d => {
    const dg = goalsByDate[d] || [];
    return dg.length > 0 && dg.every(g => g.is_completed);
  }).length;

  const now = new Date();
  const todayStr = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}`;

  const rateColor = completionRate >= 80 ? '#10b981' : completionRate >= 50 ? '#f59e0b' : '#94a3b8';
  const rateLabel = completionRate >= 80 ? 'EXCELLENT' : completionRate >= 50 ? 'ON TRACK' : 'IN PROGRESS';

  const PAD = 24; // 外側パディング

  // ============================================================
  // ① ヘッダーカード
  // ============================================================
  const HDR_Y = PAD;
  const HDR_H = 110;
  const HDR_W = W - PAD * 2;

  // カード背景
  ctx.save();
  roundRect(ctx, PAD, HDR_Y, HDR_W, HDR_H, 12);
  ctx.fillStyle = 'rgba(255,255,255,0.04)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.09)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();

  // 左アクセントバー
  ctx.save();
  roundRect(ctx, PAD, HDR_Y, 4, HDR_H, 2);
  const accentG = ctx.createLinearGradient(0, HDR_Y, 0, HDR_Y + HDR_H);
  accentG.addColorStop(0, '#0d9488');
  accentG.addColorStop(0.5, '#10b981');
  accentG.addColorStop(1, '#059669');
  ctx.fillStyle = accentG;
  ctx.fill();
  ctx.restore();

  // タイトルエリア（左）
  const titleX = PAD + 18;
  const titleY = HDR_Y + 18;

  // ラベル
  ctx.font = `700 8px 'Helvetica Neue', Arial, sans-serif`;
  ctx.fillStyle = '#0d9488';
  ctx.letterSpacing = '3px';
  ctx.fillText('WEEKLY STUDY SCHEDULE', titleX, titleY + 8);
  ctx.letterSpacing = '0px';

  // メインタイトル
  const titleText = data.username ? `${data.username}の学習スケジュール` : '週間学習スケジュール';
  ctx.font = `900 20px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
  ctx.fillStyle = '#f1f5f9';
  ctx.fillText(titleText, titleX, titleY + 32);

  // サブ情報
  ctx.font = `500 10px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
  ctx.fillStyle = 'rgba(148,163,184,0.8)';
  ctx.fillText(`📅  ${data.weekLabel}`, titleX, titleY + 52);
  ctx.fillText(`🗓  出力日: ${todayStr}`, titleX, titleY + 68);

  // 区切り線（縦）
  const divX = W - PAD - 260;
  ctx.strokeStyle = 'rgba(255,255,255,0.07)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(divX, HDR_Y + 14);
  ctx.lineTo(divX, HDR_Y + HDR_H - 14);
  ctx.stroke();

  // 達成率サークル
  const circCX = divX + 52;
  const circCY = HDR_Y + HDR_H / 2;
  drawArcProgress(ctx, circCX, circCY, 30, 6, completionRate, 'rgba(255,255,255,0.07)', rateColor);

  ctx.font = `900 16px 'Helvetica Neue', Arial, sans-serif`;
  ctx.fillStyle = '#f1f5f9';
  ctx.textAlign = 'center';
  ctx.fillText(`${completionRate}%`, circCX, circCY + 6);
  ctx.textAlign = 'left';

  ctx.font = `700 7px 'Helvetica Neue', Arial, sans-serif`;
  ctx.fillStyle = rateColor;
  ctx.textAlign = 'center';
  ctx.fillText(rateLabel, circCX, circCY + 22);
  ctx.fillStyle = 'rgba(148,163,184,0.5)';
  ctx.font = `500 7px 'Helvetica Neue', Arial, sans-serif`;
  ctx.fillText('達成率', circCX, circCY + 33);
  ctx.textAlign = 'left';

  // 4統計カード
  const statsData = [
    { icon: '📋', value: totalGoals, unit: '件', label: '総目標', color: '#a78bfa' },
    { icon: '✅', value: completedGoals, unit: '件', label: '達成済み', color: '#10b981' },
    { icon: '📆', value: activeDays, unit: '日', label: '活動日数', color: '#fbbf24' },
    { icon: '🏆', value: perfectDays, unit: '日', label: '完全達成', color: '#f472b6' },
  ];

  const statStartX = divX + 96;
  const statW = 58;
  const statH = 72;
  const statGap = 8;
  const statY = HDR_Y + (HDR_H - statH) / 2;

  statsData.forEach((s, i) => {
    const sx = statStartX + i * (statW + statGap);
    ctx.save();
    roundRect(ctx, sx, statY, statW, statH, 8);
    ctx.fillStyle = 'rgba(255,255,255,0.04)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.07)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    ctx.font = '13px serif';
    ctx.textAlign = 'center';
    ctx.fillText(s.icon, sx + statW / 2, statY + 20);

    ctx.font = `900 18px 'Helvetica Neue', Arial, sans-serif`;
    ctx.fillStyle = s.color;
    ctx.fillText(String(s.value), sx + statW / 2 - 5, statY + 42);

    ctx.font = `600 8px 'Helvetica Neue', Arial, sans-serif`;
    ctx.fillStyle = 'rgba(148,163,184,0.5)';
    ctx.fillText(s.unit, sx + statW / 2 + 10, statY + 42);

    ctx.font = `500 7px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
    ctx.fillStyle = 'rgba(148,163,184,0.5)';
    ctx.fillText(s.label, sx + statW / 2, statY + 58);
    ctx.textAlign = 'left';
  });

  // ============================================================
  // ② 週間進捗バー
  // ============================================================
  const PROG_Y = HDR_Y + HDR_H + 10;
  const PROG_H = 44;

  ctx.save();
  roundRect(ctx, PAD, PROG_Y, HDR_W, PROG_H, 10);
  ctx.fillStyle = 'rgba(255,255,255,0.04)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.07)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();

  ctx.font = `800 8px 'Helvetica Neue', Arial, sans-serif`;
  ctx.fillStyle = 'rgba(241,245,249,0.8)';
  ctx.fillText('WEEKLY PROGRESS', PAD + 14, PROG_Y + 16);

  ctx.font = `500 8px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
  ctx.fillStyle = 'rgba(148,163,184,0.5)';
  ctx.fillText(`${completedGoals} / ${totalGoals} 目標達成`, PAD + 14, PROG_Y + 28);

  ctx.font = `800 10px 'Helvetica Neue', Arial, sans-serif`;
  ctx.fillStyle = rateColor;
  ctx.textAlign = 'right';
  ctx.fillText(`${completionRate}%`, PAD + HDR_W - 14, PROG_Y + 22);
  ctx.textAlign = 'left';

  // バー
  const barX = PAD + 160;
  const barW = HDR_W - 160 - 60;
  const barY = PROG_Y + 18;
  const barH = 8;

  ctx.save();
  roundRect(ctx, barX, barY, barW, barH, 4);
  ctx.fillStyle = 'rgba(255,255,255,0.07)';
  ctx.fill();
  ctx.restore();

  if (completionRate > 0) {
    const fillW = (completionRate / 100) * barW;
    ctx.save();
    roundRect(ctx, barX, barY, fillW, barH, 4);
    const barG = ctx.createLinearGradient(barX, 0, barX + fillW, 0);
    barG.addColorStop(0, rateColor);
    barG.addColorStop(1, completionRate >= 80 ? '#059669' : completionRate >= 50 ? '#d97706' : '#64748b');
    ctx.fillStyle = barG;
    ctx.fill();
    ctx.restore();
  }

  // 目盛り
  [25, 50, 75].forEach(pct => {
    const mx = barX + (pct / 100) * barW;
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(mx, barY);
    ctx.lineTo(mx, barY + barH);
    ctx.stroke();
  });

  // ============================================================
  // ③ 日別スケジュールグリッド
  // ============================================================
  const GRID_Y = PROG_Y + PROG_H + 10;
  const GRID_H = H - GRID_Y - PAD - 32; // フッター分を確保
  const GRID_W = HDR_W;

  ctx.save();
  roundRect(ctx, PAD, GRID_Y, GRID_W, GRID_H, 12);
  ctx.fillStyle = 'rgba(255,255,255,0.03)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.07)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();

  // グリッドヘッダー
  const GHDR_H = 36;
  ctx.font = `800 11px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
  ctx.fillStyle = '#f1f5f9';
  ctx.fillText('日別スケジュール', PAD + 46, GRID_Y + 22);

  ctx.font = `500 7.5px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
  ctx.fillStyle = 'rgba(148,163,184,0.5)';
  ctx.fillText('各日の目標と達成状況', PAD + 46, GRID_Y + 33);

  // アイコン背景
  ctx.save();
  roundRect(ctx, PAD + 12, GRID_Y + 10, 24, 24, 6);
  const iconG = ctx.createLinearGradient(PAD + 12, GRID_Y + 10, PAD + 36, GRID_Y + 34);
  iconG.addColorStop(0, '#0d9488');
  iconG.addColorStop(1, '#10b981');
  ctx.fillStyle = iconG;
  ctx.fill();
  ctx.restore();
  ctx.font = '12px serif';
  ctx.textAlign = 'center';
  ctx.fillText('📅', PAD + 24, GRID_Y + 26);
  ctx.textAlign = 'left';

  // 凡例
  const legendX = PAD + GRID_W - 14;
  const legendY = GRID_Y + 20;
  const legends = [
    { color: '#10b981', label: '達成済み' },
    { color: '#f59e0b', label: '進行中' },
  ];
  let lx = legendX;
  legends.reverse().forEach(l => {
    ctx.font = `500 7.5px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
    const tw = ctx.measureText(l.label).width;
    lx -= tw + 14;
    ctx.fillStyle = 'rgba(148,163,184,0.6)';
    ctx.fillText(l.label, lx + 10, legendY);
    ctx.beginPath();
    ctx.arc(lx + 4, legendY - 4, 3, 0, 2 * Math.PI);
    ctx.fillStyle = l.color;
    ctx.fill();
    lx -= 10;
  });

  // 区切り線
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PAD + 12, GRID_Y + GHDR_H);
  ctx.lineTo(PAD + GRID_W - 12, GRID_Y + GHDR_H);
  ctx.stroke();

  // ============================================================
  // 7日カラム描画
  // ============================================================
  const COL_AREA_X = PAD + 10;
  const COL_AREA_Y = GRID_Y + GHDR_H + 8;
  const COL_AREA_W = GRID_W - 20;
  const COL_AREA_H = GRID_H - GHDR_H - 16;
  const COL_GAP = 6;
  const COL_W = (COL_AREA_W - COL_GAP * 6) / 7;

  const WEEKDAY_EN = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土'];

  data.weekDates.forEach((dateStr, colIdx) => {
    const dayGoals = goalsByDate[dateStr] || [];
    const completed = dayGoals.filter(g => g.is_completed).length;
    const total = dayGoals.length;
    const allDone = total > 0 && completed === total;
    const todayDate = isToday(dateStr);
    const d = new Date(dateStr + 'T00:00:00');
    const dow = d.getDay();
    const isSun = dow === 0;
    const isSat = dow === 6;
    const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;

    const cx = COL_AREA_X + colIdx * (COL_W + COL_GAP);
    const cy = COL_AREA_Y;
    const cw = COL_W;
    const ch = COL_AREA_H;

    // カード背景
    ctx.save();
    roundRect(ctx, cx, cy, cw, ch, 10);
    const cardBg = ctx.createLinearGradient(cx, cy, cx, cy + ch);
    cardBg.addColorStop(0, '#1a2540');
    cardBg.addColorStop(1, '#0f172a');
    ctx.fillStyle = cardBg;
    ctx.fill();

    // ボーダー
    ctx.strokeStyle = todayDate
      ? 'rgba(13,148,136,0.7)'
      : allDone
      ? 'rgba(16,185,129,0.4)'
      : 'rgba(255,255,255,0.07)';
    ctx.lineWidth = todayDate ? 1.5 : 1;
    ctx.stroke();
    ctx.restore();

    // 今日のトップライン
    if (todayDate) {
      ctx.save();
      roundRect(ctx, cx, cy, cw, 3, 2);
      const topG = ctx.createLinearGradient(cx, 0, cx + cw, 0);
      topG.addColorStop(0, '#0d9488');
      topG.addColorStop(1, '#10b981');
      ctx.fillStyle = topG;
      ctx.fill();
      ctx.restore();
    } else if (allDone) {
      ctx.save();
      roundRect(ctx, cx, cy, cw, 3, 2);
      const topG = ctx.createLinearGradient(cx, 0, cx + cw, 0);
      topG.addColorStop(0, '#34d399');
      topG.addColorStop(1, '#10b981');
      ctx.fillStyle = topG;
      ctx.fill();
      ctx.restore();
    }

    // ヘッダー背景
    const HDR_COL_H = 56;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cx + 10, cy);
    ctx.lineTo(cx + cw - 10, cy);
    ctx.arcTo(cx + cw, cy, cx + cw, cy + 10, 10);
    ctx.lineTo(cx + cw, cy + HDR_COL_H);
    ctx.lineTo(cx, cy + HDR_COL_H);
    ctx.arcTo(cx, cy, cx + 10, cy, 10);
    ctx.closePath();

    if (todayDate) {
      const hg = ctx.createLinearGradient(cx, cy, cx, cy + HDR_COL_H);
      hg.addColorStop(0, '#0d9488');
      hg.addColorStop(1, '#059669');
      ctx.fillStyle = hg;
    } else {
      const hg = ctx.createLinearGradient(cx, cy, cx, cy + HDR_COL_H);
      hg.addColorStop(0, '#1e2d45');
      hg.addColorStop(1, '#162035');
      ctx.fillStyle = hg;
    }
    ctx.fill();
    ctx.restore();

    // 曜日（英語）
    ctx.font = `700 7px 'Helvetica Neue', Arial, sans-serif`;
    ctx.fillStyle = todayDate
      ? 'rgba(255,255,255,0.85)'
      : isSun
      ? 'rgba(248,113,113,0.85)'
      : isSat
      ? 'rgba(96,165,250,0.85)'
      : 'rgba(148,163,184,0.7)';
    ctx.textAlign = 'center';
    ctx.fillText(WEEKDAY_EN[dow], cx + cw / 2, cy + 14);

    // 日付数字
    ctx.font = `900 20px 'Helvetica Neue', Arial, sans-serif`;
    ctx.fillStyle = todayDate
      ? '#ffffff'
      : isSun
      ? '#f87171'
      : isSat
      ? '#60a5fa'
      : '#e2e8f0';
    ctx.fillText(String(d.getDate()), cx + cw / 2, cy + 34);

    // 曜日（日本語）
    ctx.font = `500 7px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
    ctx.fillStyle = todayDate
      ? 'rgba(255,255,255,0.75)'
      : isSun
      ? 'rgba(248,113,113,0.7)'
      : isSat
      ? 'rgba(96,165,250,0.7)'
      : 'rgba(148,163,184,0.6)';
    ctx.fillText(`${WEEKDAY_JA[dow]}曜日`, cx + cw / 2, cy + 46);

    // TODAY バッジ
    if (todayDate) {
      const badgeW = 34;
      const badgeH = 11;
      const badgeX = cx + cw / 2 - badgeW / 2;
      const badgeY = cy + 50;
      ctx.save();
      roundRect(ctx, badgeX, badgeY, badgeW, badgeH, 5);
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.fill();
      ctx.restore();
      ctx.font = `800 6.5px 'Helvetica Neue', Arial, sans-serif`;
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.fillText('TODAY', cx + cw / 2, badgeY + 8);
    }

    // 全達成バッジ
    if (allDone && !todayDate) {
      ctx.font = '12px serif';
      ctx.fillText('✨', cx + cw / 2, cy + 52);
    }

    ctx.textAlign = 'left';

    // ============================================================
    // 目標リスト描画
    // ============================================================
    const LIST_TOP = cy + HDR_COL_H + 6;
    const LIST_BOTTOM = cy + ch - 28; // 進捗バー分を確保
    const LIST_H = LIST_BOTTOM - LIST_TOP;
    const ITEM_PAD_X = 5;
    const ITEM_W = cw - ITEM_PAD_X * 2;

    if (total === 0) {
      ctx.font = `500 7px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
      ctx.fillStyle = 'rgba(148,163,184,0.3)';
      ctx.textAlign = 'center';
      ctx.fillText('— 目標なし —', cx + cw / 2, LIST_TOP + LIST_H / 2);
      ctx.textAlign = 'left';
    } else {
      // 目標数に応じてアイテム高さを動的計算
      // 最小高さ: 22px、最大: 利用可能高さ / 目標数
      const maxItemH = Math.floor(LIST_H / total);
      const ITEM_H = Math.max(18, Math.min(maxItemH, 38));
      const TEXT_MAX_LINES = ITEM_H >= 30 ? 2 : 1;
      const LINE_H = 9;

      let iy = LIST_TOP;
      dayGoals.forEach(g => {
        if (iy + ITEM_H > LIST_BOTTOM) return; // はみ出し防止

        const ix = cx + ITEM_PAD_X;
        const iw = ITEM_W;
        const ih = ITEM_H;

        // アイテム背景
        ctx.save();
        roundRect(ctx, ix, iy, iw, ih, 4);
        ctx.fillStyle = g.is_completed
          ? 'rgba(16,185,129,0.08)'
          : 'rgba(255,255,255,0.03)';
        ctx.fill();
        ctx.restore();

        // 左ボーダー
        ctx.fillStyle = g.is_completed ? '#10b981' : 'rgba(255,255,255,0.12)';
        ctx.fillRect(ix, iy, 2, ih);

        // チェックボックス
        const cbSize = 9;
        const cbX = ix + 5;
        const cbY = iy + (ih - cbSize) / 2;
        ctx.save();
        roundRect(ctx, cbX, cbY, cbSize, cbSize, 2);
        if (g.is_completed) {
          const cbG = ctx.createLinearGradient(cbX, cbY, cbX + cbSize, cbY + cbSize);
          cbG.addColorStop(0, '#34d399');
          cbG.addColorStop(1, '#10b981');
          ctx.fillStyle = cbG;
          ctx.fill();
          ctx.font = `900 7px 'Helvetica Neue', Arial, sans-serif`;
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.fillText('✓', cbX + cbSize / 2, cbY + cbSize - 1.5);
          ctx.textAlign = 'left';
        } else {
          ctx.fillStyle = 'transparent';
          ctx.fill();
          ctx.strokeStyle = 'rgba(255,255,255,0.25)';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
        ctx.restore();

        // テキスト
        const textX = ix + 17;
        const textMaxW = iw - 22;
        const textY = iy + (ih - (TEXT_MAX_LINES * LINE_H)) / 2 + LINE_H - 1;

        ctx.font = `${g.is_completed ? '400' : '500'} 7.5px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
        ctx.fillStyle = g.is_completed
          ? 'rgba(148,163,184,0.5)'
          : 'rgba(226,232,240,0.9)';

        if (g.is_completed) {
          // 取り消し線
          ctx.save();
          const lines: string[] = [];
          const chars = g.goal_text.split('');
          let line = '';
          chars.forEach(c => {
            const test = line + c;
            if (ctx.measureText(test).width > textMaxW && line !== '') {
              lines.push(line);
              line = c;
            } else {
              line = test;
            }
          });
          if (line) lines.push(line);
          const displayLines = lines.slice(0, TEXT_MAX_LINES);
          displayLines.forEach((l, li) => {
            const ly = textY + li * LINE_H;
            ctx.fillText(l, textX, ly);
            const lw = ctx.measureText(l).width;
            ctx.strokeStyle = 'rgba(148,163,184,0.4)';
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(textX, ly - 3);
            ctx.lineTo(textX + lw, ly - 3);
            ctx.stroke();
          });
          ctx.restore();
        } else {
          drawWrappedText(ctx, g.goal_text, textX, textY, textMaxW, LINE_H, TEXT_MAX_LINES);
        }

        // 難易度ドット
        if (ITEM_H >= 22) {
          const diff = g.difficulty || 3;
          const dotY = iy + ih - 5;
          const dotStartX = ix + 17;
          for (let di = 0; di < 5; di++) {
            ctx.beginPath();
            ctx.arc(dotStartX + di * 6, dotY, 2, 0, 2 * Math.PI);
            ctx.fillStyle = di < diff ? '#fbbf24' : 'rgba(255,255,255,0.08)';
            ctx.fill();
          }
        }

        iy += ITEM_H + 3;
      });
    }

    // ============================================================
    // 進捗フッター
    // ============================================================
    if (total > 0) {
      const footY = cy + ch - 24;

      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cx + 4, footY);
      ctx.lineTo(cx + cw - 4, footY);
      ctx.stroke();

      ctx.font = `500 7px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
      ctx.fillStyle = 'rgba(148,163,184,0.6)';
      ctx.fillText(`${completed}/${total}`, cx + 6, footY + 10);

      ctx.font = `800 8px 'Helvetica Neue', Arial, sans-serif`;
      ctx.fillStyle = allDone ? '#10b981' : 'rgba(148,163,184,0.7)';
      ctx.textAlign = 'right';
      ctx.fillText(`${progressPct}%`, cx + cw - 5, footY + 10);
      ctx.textAlign = 'left';

      // ミニ進捗バー
      const mbX = cx + 4;
      const mbY = footY + 14;
      const mbW = cw - 8;
      const mbH = 3;
      ctx.save();
      roundRect(ctx, mbX, mbY, mbW, mbH, 1.5);
      ctx.fillStyle = 'rgba(255,255,255,0.07)';
      ctx.fill();
      ctx.restore();

      if (progressPct > 0) {
        const fillW = (progressPct / 100) * mbW;
        ctx.save();
        roundRect(ctx, mbX, mbY, fillW, mbH, 1.5);
        const pbG = ctx.createLinearGradient(mbX, 0, mbX + fillW, 0);
        if (allDone) {
          pbG.addColorStop(0, '#34d399');
          pbG.addColorStop(1, '#10b981');
        } else if (progressPct >= 50) {
          pbG.addColorStop(0, '#fbbf24');
          pbG.addColorStop(1, '#f97316');
        } else {
          pbG.addColorStop(0, '#64748b');
          pbG.addColorStop(1, '#94a3b8');
        }
        ctx.fillStyle = pbG;
        ctx.fill();
        ctx.restore();
      }
    }
  });

  // ============================================================
  // ④ フッター
  // ============================================================
  const FOOT_Y = H - PAD - 20;

  ctx.font = `500 8px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
  ctx.fillStyle = 'rgba(148,163,184,0.4)';
  ctx.fillText(
    data.username ? `${data.username} の学習記録` : '学習記録',
    PAD,
    FOOT_Y + 12,
  );

  const motivMsg =
    completionRate >= 80
      ? '🌟 素晴らしい達成率！Keep it up!'
      : completionRate >= 50
      ? '💪 着実に前進中！'
      : '🌱 コツコツ積み上げよう！';

  ctx.font = `700 8px 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif`;
  ctx.fillStyle = rateColor;
  ctx.textAlign = 'center';
  ctx.fillText(motivMsg, W / 2, FOOT_Y + 12);

  ctx.font = `500 7px 'Helvetica Neue', Arial, sans-serif`;
  ctx.fillStyle = 'rgba(148,163,184,0.3)';
  ctx.textAlign = 'right';
  ctx.fillText(`${todayStr} 出力`, W - PAD, FOOT_Y + 12);
  ctx.textAlign = 'left';

  return canvas;
}

// ============================================================
// エクスポート関数
// ============================================================
export const exportScheduleToPDF = async (data: ScheduleExportData): Promise<void> => {
  const { default: jsPDF } = await import('jspdf');

  const canvas = drawSchedulePDF(data);

  const imgData = canvas.toDataURL('image/jpeg', 0.97);
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);

  const now = new Date();
  const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  pdf.save(`学習スケジュール_${dateStr}.pdf`);
};
